import { AfterViewInit, Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { API, LoginRestoreDTO, PasswordRequirementDTO } from '../../services/api/api';
import { ErrorService } from '../../services/error.service';
import { QueryParamService } from '../../services/lists/query-params';
import { Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { take, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StepperComponent } from '@ngx-seatback/layout';
import { interval, Subject } from 'rxjs';


const MAX_CODE_REQUEST_INTERVAL = 30;

@Component({
  selector: 'sb-page-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss'],
  standalone: false,
})
export class PasswordResetComponent {
  public passwordRequirements: { [key: string]: number } | null;

  public loading: boolean;
  public showInboxHint: boolean;
  public isInvalidRestoreCode: boolean;
  public requestingRestoreCode: boolean;
  public errorMessage: string | undefined;
  public restoreCodeRequestBlocked: number;
  public matchingPassword: boolean | undefined;
  public passwordRequirementsFulfilled: boolean;
  public showPasswordRequirementsAsError: boolean;

  private _stepper: StepperComponent;
  private navigatedToPassword: Subject<boolean>;

  public formRestoreCode: FormGroup = new FormGroup({
    code: new FormControl('', [Validators.required]),
    username: new FormControl('', [Validators.required]),
  });

  public formPassword = new UntypedFormGroup({
    password: new UntypedFormControl('', [Validators.required]),
    passwordCheck: new UntypedFormControl('', [Validators.required]),
  });

  constructor(
    public router: Router,
    private api: API,
    private errorHandler: ErrorService,
    private queryParamService: QueryParamService,
    public userStatus: UserService,
    private destroyRef: DestroyRef
  ) {
    this.loading = false;
    this.showInboxHint = false;
    this.passwordRequirements = null;
    this.isInvalidRestoreCode = false;
    this.requestingRestoreCode = false;
    this.restoreCodeRequestBlocked = -1;
    this.showPasswordRequirementsAsError = false;
    this.navigatedToPassword = new Subject<boolean>();
  }

  get Username() {
    return this.formRestoreCode.get('username');
  }

  get RestoreCode() {
    return this.formRestoreCode.get('code');
  }

  get Password() {
    return this.formPassword.get('password');
  }

  get PasswordCheck() {
    return this.formPassword.get('passwordCheck');
  }

  @ViewChild('stepperComponent')
  public set stepper(component: StepperComponent | undefined) {
    if (!component || this._stepper === component) {
      return;
    }

    if (this._stepper) {
      this._stepper.stepperService.stepVerificationSubject.unsubscribe();
    }

    this._stepper = component;

    this.init();
  }

  public get stepper(): StepperComponent {
    return this._stepper;
  }

  private init(): void {
    if (this.stepper) {
      this.stepper.stepperService.stepVerificationSubject.subscribe(index => {
        if (index === null) {
          return;
        }

        this.loading = true;
        this.validateStep(index).then((valid: boolean) => {
          this.loading = false;
          this.stepper.stepperService.verifyStep(index, valid);
        });
      });
    }

    const pUsername = this.queryParamService.getQueryParam('username');
    if (pUsername) {
      this.Username.setValue(pUsername);
      const pToken = this.queryParamService.getQueryParam('token');
      if (pToken) {
        this.RestoreCode.setValue(pToken);

        this.stepper.stepperService.requestNavigation(1);
      }
    }
  }

  private validateStep(index: number): Promise<boolean> {
    return new Promise(resolve => {
      if (index === 0) {
        this.matchingPassword = undefined;
        this.showPasswordRequirementsAsError = false;

        this.formRestoreCode.markAllAsTouched();
        if (!this.formRestoreCode.valid) {
          return resolve(false);
        }

        this.verifyRestoreCode(this.Username.value, this.RestoreCode.value).then((valid: boolean) => {
          if (valid) {
            this.isInvalidRestoreCode = false;
            this.fetchPasswordRequirements()
              .then(() => {
                this.navigatedToPassword.next(true);
                resolve(true);
              })
              .catch(() => resolve(false));
          } else {
            this.showInboxHint = false;
            resolve(false);
          }
        });
      } else if (index === 1) {
        this.errorMessage = undefined;
        this.formPassword.markAllAsTouched();
        this.matchingPassword = this.Password.value === this.PasswordCheck.value;

        if (!this.passwordRequirementsFulfilled || !this.Password.value) {
          this.showPasswordRequirementsAsError = true;
          return resolve(false);
        }

        if (!this.matchingPassword) {
          return resolve(false);
        }

        this.restorePassword(this.Username.value, this.Password.value, this.RestoreCode.value)
          .then((valid: boolean) => {
            resolve(valid);
            setTimeout(() => {
              this.Password.reset('');
              this.PasswordCheck.reset('');
              this.navigateToLogin();
            }, 200);
          })
          .catch((error: any) => {
            if (error?.status && (error.status >= 400 || error.status < 500)) {
              this.errorMessage = 'Please check your credentials.';
            } else {
              this.errorHandler.errorHandler(error);
            }
            resolve(false);
          });
      } else {
        resolve(false);
      }
    });
  }

  private fetchPasswordRequirements(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api
        .auth_Authentication_passwordRequirements()
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (req: PasswordRequirementDTO) => {
            this.passwordRequirements = req as any;
            resolve(null);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
            reject(error);
          },
        });
    });
  }

  requestRestoreCode(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestingRestoreCode = true;

      this.api
        .auth_Authentication_forgotPassword({ username: this.Username.value })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: () => {
            this.queryParamService.clearQueryParam('token');
            this.queryParamService.setQueryParam('username', this.Username.value);

            this.showInboxHint = true;
            this.requestingRestoreCode = false;

            this.restoreCodeRequestBlocked = MAX_CODE_REQUEST_INTERVAL;
            interval(1000)
              .pipe(take(MAX_CODE_REQUEST_INTERVAL), takeUntil(this.navigatedToPassword))
              .subscribe(x => {
                this.restoreCodeRequestBlocked -= 1;
              });

            resolve(null);
          },
          error: error => {
            this.requestingRestoreCode = false;
            this.errorHandler.errorHandler(error);
            reject(error);
          },
        });
    });
  }

  verifyRestoreCode(username: string, restore: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (restore) {
        // trimming of restore code to avoid copy paste errors
        restore = restore.trim();
      }
      this.api
        .auth_Authentication_checkRestore({ username, restore })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: () => {
            this.isInvalidRestoreCode = false;
            this.queryParamService.setQueryParam('token', restore);
            resolve(true);
          },
          error: error => {
            this.isInvalidRestoreCode = true;
            resolve(false);
          },
        });
    });
  }

  restorePassword(username: string, password: string, code: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const dto: LoginRestoreDTO = this.api.createDTO('LoginRestoreDTO');
      dto.password = password;
      dto.username = username;
      dto.restore = code;
      this.api
        .auth_Authentication_restorePassword(dto)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: () => {
            resolve(true);
          },
          error: error => {
            reject(error);
          },
        });
    });
  }

  public navigateToLogin(): void {
    this.router.navigate(['/login']).then();
  }
}
